import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { useNeedStore } from "../store/needStore";
import { useUserDataStore } from "../store/userDataStore";
import { useLanguageStore, translations, type Need } from "../store/languageStore";
import { analytics } from "../utils/analytics";
import StepIndicator from "../components/StepIndicator";

const steps = [
  {
    number: 1,
    label: translations.stepLabels.selectNeed,
  },
  {
    number: 2,
    label: translations.stepLabels.matchSupport,
  },
  {
    number: 3,
    label: translations.stepLabels.exploreAid,
  },
];

function HomePage() {
  const navigate = useNavigate();
  const [zipCode, setZipCode] = useState("");
  const [showError, setShowError] = useState(false);
  const { selectedNeed, setSelectedNeed } = useNeedStore();
  const { setZipCode: setStoredZipCode } = useUserDataStore();
  const { selectedLanguage } = useLanguageStore();

  useEffect(() => {
    analytics.track("home_page_viewed", "navigation", {
      language: selectedLanguage,
    });
  }, [selectedLanguage]);

  const handleFindHelp = () => {
    if (!zipCode) {
      setShowError(true);
      return;
    }
    setStoredZipCode(zipCode);
    navigate("/match");
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipCode(e.target.value);
    setShowError(false);
  };

  const handleNeedSelect = (need: Need) => {
    setSelectedNeed(need);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10" />
          <div className="mt-48 sm:mt-20">
            <StepIndicator steps={steps} currentStep={1} />
          </div>
          
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mt-32 sm:mt-12 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {translations.common.back[selectedLanguage]}
          </button>

          {/* Welcome Message */}
          <div className="text-center mb-12">
            <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              {translations.home.title[selectedLanguage]}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {translations.home.subtitle[selectedLanguage]}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8">
            <div className="mb-6">
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translations.common.zipCode[selectedLanguage]}
              </label>
              <input
                id="zipCode"
                type="text"
                value={zipCode}
                onChange={handleZipChange}
                placeholder={translations.common.zipCode[selectedLanguage]}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                aria-label={translations.common.zipCode[selectedLanguage]}
              />
              {showError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {translations.common.zipCode[selectedLanguage]}
                </p>
              )}
            </div>
            <div className="mb-6">
              <label htmlFor="need" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translations.selectNeed[selectedLanguage]}
              </label>
              <select
                id="need"
                value={selectedNeed || ''}
                onChange={e => handleNeedSelect(e.target.value as Need)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
              >
                <option value="" disabled>
                  {translations.selectNeed[selectedLanguage]}
                </option>
                {Object.entries(translations.needs).map(([key, need]) => (
                  <option key={key} value={key}>
                    {need[selectedLanguage]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-center mb-4">
              <span className="flex items-center text-blue-600 dark:text-blue-300 text-sm gap-2">
                <Heart className="w-4 h-4" />
                {translations.home.subtitle[selectedLanguage]}
              </span>
            </div>
            <button
              onClick={handleFindHelp}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
            >
              {translations.common.getHelp[selectedLanguage]}
            </button>
          </div>

          <div className="mt-8 sm:mt-8 md:mt-8 lg:mt-8 xl:mt-8 2xl:mt-8 sm:relative">
            <div className="text-center text-gray-500 text-sm mt-48 sm:mt-16">
              {translations.common.poweredByBolt[selectedLanguage]}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
